/**
 * Unified Carousel Web Component — CSS Scroll Snap + Clone-based Infinite Loop
 * @extends HTMLElement
 * @fires carousel:slide-change { index, slide }
 * @fires carousel:slideChange { index, slide } (camelCase alias for PDP compat)
 * @fires carousel:reach-start
 * @fires carousel:reach-end
 * @fires carousel:destroy
 */
class Carousel extends HTMLElement {
  /**
   * Silencio de scroll que se considera "el movimiento termino", en ms. Tiene que superar el
   * hueco entre eventos de un momentum de touch sin volverse perceptible como demora del salto.
   */
  static #SCROLL_IDLE_MS = 140;

  #scroller = null;
  #slides = [];
  #clones = { start: [], end: [] };
  #currentIndex = 0;
  #isScrolling = false;
  #isJumping = false;
  #isWrapping = false;
  #jumpGen = 0;
  #normalizeTimer = null;
  #isPointerDown = false;
  #autoplayTimer = null;
  #abortController = new AbortController();
  #total = 0;
  #isLoop = false;
  #slidesPerView = 1;

  get logicalIndex() { return this.getCurrentIndex(); }
  set logicalIndex(v) { this.scrollToLogicalIndex(v, false); }

  static get observedAttributes() {
    return [
      'loop', 'columns-desktop', 'columns-mobile', 'gap',
      'show-arrows', 'show-dots', 'show-thumbs-arrows',
      'thumbs-mobile', 'thumbs-desktop', 'autoplay', 'autoplay-speed',
      'lazy-init',
    ];
  }

  #lazyObserver = null;
  #isInitialized = false;
  /** Cached `offsetLeft` of every slide. `null` means "recompute on next read". */
  #offsets = null;
  #offsetsWidth = null;
  #sizeObserver = null;

  connectedCallback() {
    if (this.hasAttribute('lazy-init')) {
      this.classList.add('carousel--pending');
      this.#createLazyObserver();
    } else {
      this.#init();
    }
    const s = this.#abortController.signal;
    this.addEventListener('mouseenter', () => this.stopAutoplay(), { signal: s });
    this.addEventListener('mouseleave', () => this.startAutoplay(), { signal: s });
    this.addEventListener('focusin', () => this.stopAutoplay(), { signal: s });
    this.addEventListener('focusout', () => this.startAutoplay(), { signal: s });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoplay(); else this.startAutoplay();
    }, { signal: s });
    window.addEventListener('resize', () => {
      this.#invalidateOffsets();
      this.#slidesPerView = this.#calculateSlidesPerView();
      if (this.#isInitialized) {
        this.#init();
      }
    }, { signal: s });
  }

  #createLazyObserver() {
    this.#lazyObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.classList.remove('carousel--pending');
          this.#init();
          this.#lazyObserver?.disconnect();
          this.#lazyObserver = null;
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.#lazyObserver.observe(this);
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#lazyObserver?.disconnect();
    this.#lazyObserver = null;
    this.#sizeObserver?.disconnect();
    this.#sizeObserver = null;
    this.#invalidateOffsets();
    this.#isInitialized = false;
    this.stopAutoplay();
    this.dispatchEvent(new CustomEvent('carousel:destroy', { bubbles: true }));
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (['loop', 'autoplay', 'autoplay-speed'].includes(name) && this.isConnected) this.#init();
    this.#renderAttributes();
  }

  #init() {
    if (this.#isInitialized) return;
    this.#isInitialized = true;

    this.#scroller = this.querySelector('[role="list"]');
    if (!this.#scroller) return;

    // Slide widths are `flex: 0 0 calc((100% - (spv - 1) * gap) / spv)`, so every `offsetLeft`
    // is a pure function of the scroller's width. Watching that one box is therefore enough to
    // know when the cached offsets stop being valid.
    this.#sizeObserver?.disconnect();
    this.#sizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;

      const borderBoxSize = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize[0] : entry.borderBoxSize;
      const width = borderBoxSize?.inlineSize ?? entry.contentRect.width;
      if (this.#offsetsWidth !== null && Math.abs(width - this.#offsetsWidth) < 0.01) return;

      this.#invalidateOffsets();
    });
    this.#sizeObserver.observe(this.#scroller);

    this.#clones.start.forEach(c => c.remove());
    this.#clones.end.forEach(c => c.remove());
    this.#clones = { start: [], end: [] };

    this.#slides = this.#scroller.querySelectorAll('[role="listitem"]');
    this.#total = this.#slides.length;
    this.#isLoop = this.getAttribute('loop') === 'true' && this.#total > 1;
    this.#slidesPerView = this.#calculateSlidesPerView();

    this.#setupScrollSnap();
    if (this.#isLoop) this.#buildClones();
    this.#buildSentinels();
    this.#buildDots();
    this.#setupThumbs();
    this.#buildThumbsArrows();
    this.#setupArrows();
    this.#setupKeyboard();
    this.startAutoplay();
    this.#renderAttributes();

    if (this.#isLoop) {
      const startClones = this.#scroller.querySelectorAll('[data-clone="start"]');
      this.#currentIndex = startClones.length;
    } else {
      this.#currentIndex = 0;
    }

    this.#syncCurrentSlidePosition();

    requestAnimationFrame(() => {
      this.#updateControls();
    });
  }

  #calculateSlidesPerView() {
    const isDesktop = window.innerWidth >= 768;
    const attr = isDesktop ? 'columns-desktop' : 'columns-mobile';
    return parseFloat(this.getAttribute(attr) ?? '1') || 1;
  }

  /**
   * Cuantos slides hay antes del primero real. Se lee de los clones construidos, no se recalcula:
   * si se derivara de #slidesPerView otra vez, un cambio en #getCloneCount desalinearia todos los
   * indices sin que nada lo delate.
   */
  #getLogicalOffset() {
    return this.#isLoop ? this.#clones.start.length : 0;
  }

  /**
   * Pasa un indice del DOM al indice real de la foto, trayendo los clones a su original.
   *
   * Hace falta porque el teleport fuera de la zona de clones ahora espera a que el scroll pare:
   * en ese rato el indice del DOM apunta a un clon, y sin este modulo los dots se quedarian sin
   * ninguno activo justo mientras el usuario mira la foto que acaba de traer.
   */
  #toLogicalIndex(domIndex) {
    const logical = domIndex - this.#getLogicalOffset();
    if (!this.#isLoop || this.#total <= 0) return logical;
    return ((logical % this.#total) + this.#total) % this.#total;
  }

  #syncCurrentSlidePosition() {
    if (!this.#scroller) return;

    const slide = this.#slides[this.#currentIndex];
    if (!slide) return;

    // Read geometry before writing styles. Setting `scrollSnapType` invalidates layout, so reading
    // `offsetLeft` after it forced a synchronous reflow on every call — this was the single largest
    // theme-owned forced reflow in the trace (351ms). Snapping does not affect the slide's offset,
    // so reading it first is equivalent.
    const left = this.#getSlideOffsets()[this.#currentIndex];

    const originalSnap = this.#scroller.style.scrollSnapType;
    this.#scroller.style.scrollSnapType = 'none';
    this.#scroller.scrollTo({ left, behavior: 'instant' });
    this.#restoreScrollSnap(originalSnap);
  }

  /**
   * Cuantos slides se clonan a cada lado.
   *
   * Con un solo clon, pararse en el se queda sin pista: ese clon es el ultimo elemento del
   * scroller, asi que si el usuario sigue deslizando antes de que el teleport ocurra no tiene a
   * donde ir y el carrusel se siente trabado. Pasaba justo al pasar de la ultima foto a la
   * primera y seguir de largo rapido.
   *
   * Con margen de sobra el teleport nunca es una condicion para poder seguir: siempre hay slide
   * por delante mientras se resuelve. El tope es #total porque mas alla de eso los clones se
   * repetirian, y ese caso no aporta nada.
   */
  #getCloneCount() {
    return Math.min(this.#total, Math.max(2, Math.ceil(this.#slidesPerView) + 1));
  }

  #buildClones() {
    const count = this.#getCloneCount();

    for (let i = 0; i < count; i++) {
      const clone = this.#slides[i].cloneNode(true);
      clone.setAttribute('data-clone', 'end');
      clone.setAttribute('aria-hidden', 'true');
      this.#scroller.appendChild(clone);
      this.#clones.end.push(clone);
    }

    for (let i = this.#total - 1; i >= this.#total - count; i--) {
      const clone = this.#slides[i].cloneNode(true);
      clone.setAttribute('data-clone', 'start');
      clone.setAttribute('aria-hidden', 'true');
      this.#scroller.insertBefore(clone, this.#scroller.firstChild);
      this.#clones.start.push(clone);
    }

    this.#slides = this.#scroller.querySelectorAll('[role="listitem"]');
    this.#invalidateOffsets();
  }

  #buildSentinels() {
    if (!this.#isLoop) return;
    const startClones = this.#scroller.querySelectorAll('[data-clone="start"]');
    const endClones = this.#scroller.querySelectorAll('[data-clone="end"]');
    startClones[0]?.setAttribute('data-sentinel', 'first');
    endClones[endClones.length - 1]?.setAttribute('data-sentinel', 'last');
  }

  #buildDots() {
    this.querySelector('.carousel-dots')?.remove();
    if (this.#total <= 1) return;
    const s = this.#abortController.signal;
    const container = Object.assign(document.createElement('div'), {
      className: 'carousel-dots',
      role: 'tablist'
    });
    for (let i = 0; i < this.#total; i++) {
      const btn = Object.assign(document.createElement('button'), { type: 'button', role: 'tab' });
      btn.setAttribute('aria-label', (this.dataset?.slideLabel || 'Go to slide __n__').replace('__n__', i + 1));
      const slide = this.#slides[i];
      if (slide) {
        slide.id ||= `${this.id || 'carousel'}-slide-${i}`;
        btn.setAttribute('aria-controls', slide.id);
      }
      btn.addEventListener('click', () => {
        this.stopAutoplay();
        this.scrollToLogicalIndex(i);
      }, { signal: s });
      container.appendChild(btn);
    }
    this.appendChild(container);
  }

  #setupThumbs() {
    const thumbs = Array.from(this.querySelectorAll('.carousel-thumb'));
    const s = this.#abortController.signal;

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        this.stopAutoplay();
        this.scrollToLogicalIndex(index);
      }, { signal: s });
    });
  }

  #buildThumbsArrows() {
    if (this.getAttribute('show-thumbs-arrows') !== 'true') return;

    // Remove existing nav wrapper if present (re-init safety)
    const existingNav = this.querySelector('.carousel-thumbs-nav');
    if (existingNav) {
      const thumbsInside = existingNav.querySelector('.carousel-thumbs');
      if (thumbsInside) existingNav.before(thumbsInside);
      existingNav.remove();
    }

    const thumbsContainer = this.querySelector('.carousel-thumbs');
    if (!thumbsContainer) return;

    const thumbCount = thumbsContainer.querySelectorAll('.carousel-thumb').length;
    if (thumbCount <= 1) return;

    // Wrap thumbs in a nav container
    const nav = document.createElement('div');
    nav.className = 'carousel-thumbs-nav';
    thumbsContainer.before(nav);
    nav.appendChild(thumbsContainer);

    // Create arrows
    const prevArrow = this.#createThumbArrow('prev', 'Previous');
    const nextArrow = this.#createThumbArrow('next', 'Next');

    nav.insertBefore(prevArrow, thumbsContainer);
    nav.appendChild(nextArrow);
  }

  #createThumbArrow(direction, label) {
    const arrow = document.createElement('button');
    arrow.type = 'button';
    arrow.className = `carousel-thumbs-arrow carousel-thumbs-arrow--${direction}`;
    arrow.setAttribute('aria-label', label);

    const svgContent = direction === 'prev'
      ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>'
      : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"></polyline></svg>';

    arrow.innerHTML = svgContent;
    arrow.addEventListener('click', () => {
      this.stopAutoplay();
      this.scrollStep(direction === 'prev' ? -1 : 1);
    });

    return arrow;
  }

  #setupScrollSnap() {
    if (!this.#scroller) return;
    const gap = parseFloat(this.getAttribute('gap') ?? '16') ?? 16;
    const spv = this.#slidesPerView;

    Object.assign(this.#scroller.style, {
      scrollSnapType: 'x mandatory',
      overflowX: 'auto',
      overflowY: 'hidden',
      scrollbarWidth: 'none',
      WebkitOverflowScrolling: 'touch',
      gap: `${gap}px`,
    });

    const basis = `calc((100% - (${spv} - 1) * ${gap}px) / ${spv})`;
    this.#slides.forEach(s => Object.assign(s.style, {
      scrollSnapAlign: 'start',
      flex: `0 0 ${basis}`,
      marginRight: '0',
    }));

    this.#scroller.addEventListener('scroll', () => {
      if (this.#isLoop) {
        this.#onScroll();
        return;
      }

      this.updateActiveIndex();
    }, {
      signal: this.#abortController.signal
    });

    // Mientras haya un dedo apoyado, el navegador esta arrastrando el scroller y lleva su propia
    // posicion interna. Escribir scrollLeft ahi no la reemplaza: al seguir moviendo el dedo el
    // gesto continua desde la posicion del navegador, no desde la nuestra, y el carrusel salta o
    // se queda trabado. Solo pasa con touch, y solo a veces — hace falta que el debounce venza
    // durante el gesto, o sea que el dedo se quede quieto un momento sin soltar.
    //
    // Se rastrea el gesto para que el teleport espere a que la pantalla este libre. `passive`
    // porque no se cancela nada: el scroll nativo tiene que seguir siendo el que manda.
    const gesture = { passive: true, signal: this.#abortController.signal };

    this.#scroller.addEventListener('touchstart', () => {
      this.#isPointerDown = true;
      clearTimeout(this.#normalizeTimer);
    }, gesture);

    const endGesture = () => {
      this.#isPointerDown = false;
      // Recien ahora vale agendar: puede que el scroll ya no emita mas eventos, si el dedo se
      // levanto sin inercia, y en ese caso este es el unico disparador que queda.
      if (this.#isLoop) this.#scheduleLoopNormalize();
    };

    this.#scroller.addEventListener('touchend', endGesture, gesture);
    this.#scroller.addEventListener('touchcancel', endGesture, gesture);

    // `scrollend` es el navegador diciendo que el movimiento termino, asi que el salto puede
    // ocurrir en ese mismo instante en vez de esperar a que venza el debounce. El debounce queda
    // de respaldo para Safari, que recien trae el evento desde la 18.
    this.#scroller.addEventListener('scrollend', () => {
      if (!this.#isLoop || this.#isPointerDown) return;
      clearTimeout(this.#normalizeTimer);
      this.#normalizeLoopNow();
    }, gesture);
  }

  #restoreScrollSnap(originalSnap) {
    const restore = () => {
      this.#scroller.style.scrollSnapType = originalSnap || 'x mandatory';
    };

    requestAnimationFrame(restore);
  }

  #setupArrows() {
    const s = this.#abortController.signal;
    const prevBtn = this.querySelector('[name="previous"]');
    const nextBtn = this.querySelector('[name="next"]');

    prevBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.stopAutoplay();
      this.scrollStep(-1);
    }, { signal: s });

    nextBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.stopAutoplay();
      this.scrollStep(1);
    }, { signal: s });
  }

  #setupKeyboard() {
    if (!this.#scroller) return;
    this.#scroller.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.scrollStep(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.scrollStep(1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        this.scrollToLogicalIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        this.scrollToLogicalIndex(this.#total - 1);
      }
    }, { signal: this.#abortController.signal });
  }

  #onScroll() {
    if (this.#isWrapping) return;

    // Los wraps de abajo son para el scroll del usuario, no para un desplazamiento que el
    // componente mismo pidio. Este guard estaba mas abajo, despues de los dos bloques de wrap,
    // y ahi llegaba tarde: al pasar de la ultima foto a la primera, #jumpTo lanza un scroll
    // `smooth` hacia el primer clon, ese scroll dispara #onScroll en cada frame, y apenas el
    // slide mas cercano pasaba a ser un clon el wrap hacia un `scrollTo` instantaneo al slide
    // real — con el scroll suave todavia corriendo hacia su destino original. Los dos se
    // peleaban frame a frame y el carrusel titilaba.
    //
    // Con el guard aca, durante un jump programatico no se wrapea: la normalizacion la hace
    // #jumpTo al terminar, via #normalizeLoopPosition, cuando el salto ya es invisible.
    if (this.#isJumping) return;

    const currentIndex = this.#findClosestSlideIndex(this.#scroller.scrollLeft);

    // El indice se actualiza al instante aunque la posicion este sobre un clon: #toLogicalIndex
    // lo trae al rango real, asi que los dots y los thumbs siguen al dedo sin esperar nada.
    if (currentIndex !== this.#currentIndex) {
      this.#currentIndex = currentIndex;
      this.#updateControls();
      this.#dispatch();
    }

    // Lo unico que se pospone es el teleport: mover scrollLeft mientras el scroll sigue vivo es
    // lo que hacia titilar el carrusel con touch. Ver #scheduleLoopNormalize.
    if (this.#isLoop) this.#scheduleLoopNormalize();
  }

  /**
   * Agenda el teleport fuera de la zona de clones para cuando el scroll se haya detenido.
   *
   * El wrap se hacia apenas #onScroll veia un clon, en pleno movimiento. Con las flechas casi no
   * se notaba, pero el swipe entra por scroll nativo con momentum: escribir scrollLeft mientras
   * el navegador sigue aplicando inercia lo deja peleando contra su propia animacion. Peor aun,
   * el wrap se protegia con un timeout de 300ms, y el momentum dura bastante mas — al vencer el
   * timeout volvia a detectar el clon y disparaba otro wrap, y otro. Eso es el "titila un rato".
   *
   * El debounce se reinicia con cada evento de scroll, asi que solo corre cuando el movimiento
   * termino de verdad. No se usa `scrollend` porque Safari recien lo trae desde la 18 y ahi el
   * fallback por tiempo volveria a cortar en medio del momentum.
   *
   * Mientras tanto el usuario esta parado sobre un clon, que es el mismo markup que su original:
   * no hay nada que ver hasta que el salto ocurre.
   */
  #scheduleLoopNormalize() {
    clearTimeout(this.#normalizeTimer);
    this.#normalizeTimer = setTimeout(() => this.#normalizeLoopNow(), Carousel.#SCROLL_IDLE_MS);
  }

  /**
   * Ejecuta el salto fuera de la zona de clones, si corresponde. Lo llaman el debounce y el
   * evento `scrollend`: los dos significan lo mismo, que el scroll ya no se esta moviendo.
   */
  #normalizeLoopNow() {
    if (!this.#scroller || this.#isJumping || this.#isWrapping) return;

    // El dedo sigue en la pantalla: el gesto manda. touchend vuelve a agendar esto.
    if (this.#isPointerDown) return;

    const offset = this.#getLogicalOffset();
    const inClones = this.#currentIndex < offset || this.#currentIndex >= this.#total + offset;
    if (!inClones) return;

    this.#isWrapping = true;

    const originalSnap = this.#scroller.style.scrollSnapType;
    this.#scroller.style.scrollSnapType = 'none';
    this.#normalizeLoopPosition();
    this.#restoreScrollSnap(originalSnap);

    this.#updateControls();
    this.#dispatch();

    // El teleport emite su propio evento de scroll. Liberar el flag recien en el frame
    // siguiente evita que ese evento reagende otra normalizacion sobre la posicion ya buena.
    requestAnimationFrame(() => { this.#isWrapping = false; });
  }

  /**
   * Si la posicion quedo sobre un clon, la lleva al slide real equivalente.
   *
   * Se llama con el scroll ya detenido y `scroll-snap` todavia en `none`, asi que el salto no
   * se ve: el clon y su original son el mismo markup, y el desplazamiento es de exactamente un
   * bloque de slides. Es el mismo teleport que hace #onScroll para el swipe del usuario, pero
   * al final del movimiento en vez de en medio.
   */
  #normalizeLoopPosition() {
    if (!this.#isLoop || !this.#scroller) return;

    const offset = this.#getLogicalOffset();
    const firstEndClone = this.#total + offset;

    let target = null;
    if (this.#currentIndex >= firstEndClone) target = this.#currentIndex - this.#total;
    else if (this.#currentIndex < offset) target = this.#currentIndex + this.#total;

    if (target === null) return;

    const slide = this.#slides[target];
    if (!slide) return;

    this.#scroller.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
    this.#currentIndex = target;
  }

  scrollStep(direction) {
    if (this.#isJumping) return;
    const next = this.#currentIndex + direction;
    this.#jumpTo(next, true);
  }

  scrollToIndex(index, smooth = true) {
    const targetIndex = index + this.#getLogicalOffset();
    if (targetIndex < 0 || targetIndex >= this.#slides.length) return;
    this.#jumpTo(targetIndex, smooth);
  }

  scrollToLogicalIndex(index, smooth = true) {
    this.scrollToIndex(index, smooth);
  }

  #jumpTo(index, smooth) {
    if (!this.#scroller) return;
    const slide = this.#slides[index];
    if (!slide) return;

    this.#isJumping = true;
    const gen = ++this.#jumpGen;

    // Geometry first, then style writes — otherwise this read forces a synchronous reflow.
    const targetLeft = slide.offsetLeft;

    const originalSnap = this.#scroller.style.scrollSnapType;
    this.#scroller.style.scrollSnapType = 'none';

    this.#scroller.scrollTo({
      left: targetLeft,
      behavior: smooth ? 'smooth' : 'instant'
    });

    const fallbackMs = smooth ? 300 : 50;
    this.#waitForScrollEnd(() => {
      if (gen !== this.#jumpGen) return;
      this.#currentIndex = index;

      // Antes de restaurar el snap: #restoreScrollSnap difiere la escritura un frame, y con el
      // snap ya activo el scrollTo instantaneo del teleport pelearia contra el.
      this.#normalizeLoopPosition();

      this.#restoreScrollSnap(originalSnap);
      this.#updateControls();
      this.#dispatch();
      this.#isJumping = false;
    }, fallbackMs);
  }

  #updateControls() {
    const realIndex = this.#toLogicalIndex(this.#currentIndex);

    // Sync dots
    this.querySelectorAll('.carousel-dots button').forEach((d, i) => {
      d.setAttribute('aria-selected', String(i === realIndex));
    });

    // Sync thumbs & auto-scroll active into view
    const thumbs = this.querySelectorAll('.carousel-thumb');
    if (thumbs.length > 0) {
      thumbs.forEach((thumb, index) => {
        if (index === realIndex) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      const activeThumb = thumbs[realIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }

    const prev = this.querySelector('[name="previous"]');
    const next = this.querySelector('[name="next"]');

    if (prev) prev.disabled = false;
    if (next) next.disabled = false;

    if (prev && next && !this.#isLoop && this.#scroller) {
      if (this.#total <= this.#slidesPerView) {
        prev.disabled = true;
        next.disabled = true;
        return;
      }

      const maxScrollLeft = Math.max(0, this.#scroller.scrollWidth - this.#scroller.clientWidth);
      const currentScrollLeft = this.#scroller.scrollLeft;
      const epsilon = 1;

      if (maxScrollLeft <= epsilon) {
        prev.disabled = true;
        next.disabled = this.#total <= this.#slidesPerView;
        return;
      }

      prev.disabled = currentScrollLeft <= epsilon;
      next.disabled = currentScrollLeft >= maxScrollLeft - epsilon;
    }
  }

  #announce() {
    let live = this.querySelector('[aria-live]');
    if (!live) {
      live = document.createElement('div');
      live.className = 'sr-only';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      Object.assign(live.style, {
        position: 'absolute',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        clip: 'rect(0,0,0,0)'
      });
      this.appendChild(live);
    }
    const realIndex = this.#toLogicalIndex(this.#currentIndex);
    const slide = this.#slides[this.#currentIndex];
    const title = slide?.querySelector('.product-card-simple__title, .product-title')?.textContent?.trim();
    live.textContent = title
      ? `Producto ${realIndex + 1} de ${this.#total}: ${title}`
      : `Producto ${realIndex + 1} de ${this.#total}`;
  }

  #dispatch() {
    const realIndex = this.#toLogicalIndex(this.#currentIndex);
    const slide = this.#slides[this.#currentIndex];
    const detail = { index: realIndex, slide };
    this.dispatchEvent(new CustomEvent('carousel:slide-change', { detail, bubbles: true }));
    this.dispatchEvent(new CustomEvent('carousel:slideChange', { detail, bubbles: true }));
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.getAttribute('autoplay') !== 'true' || this.#total <= 1) return;
    const speed = parseInt(this.getAttribute('autoplay-speed') || '5000', 10);
    this.#autoplayTimer = setInterval(() => {
      this.scrollStep(1);
    }, speed);
  }

  stopAutoplay() {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }

  getCurrentIndex() {
    return this.#toLogicalIndex(this.#currentIndex);
  }

  getTotalSlides() {
    return this.#total;
  }

  isLooping() {
    return this.#isLoop;
  }

  /**
   * Resincroniza el indice con la posicion real del scroll. Es API publica: la usa el zoom de la
   * galeria de PDP al soltar (ver blocks/_gco-product-gallery-pdp.liquid).
   *
   * En loop no puede clampear al rango de fotos reales. Estando sobre un clon, el clamp devolvia
   * la ultima foto: los dots marcaban la 4ta mientras en pantalla se veia la primera, y peor,
   * dejaba #currentIndex apuntando a un slide real, asi que la normalizacion diferida concluia
   * que no habia nada que mover y el carrusel quedaba clavado contra el tope del scroller.
   *
   * El unico camino correcto en loop es el mismo que usa el scroll nativo, que sabe de clones.
   */
  updateActiveIndex() {
    if (!this.#scroller) return;

    if (this.#isLoop) {
      this.#onScroll();
      return;
    }

    // Sin loop no hay clones, y el indice del DOM ya es el indice real.
    const closestIndex = Math.max(0, Math.min(this.#total - 1, this.#findClosestSlideIndex(this.#scroller.scrollLeft)));
    if (closestIndex !== this.#currentIndex) {
      this.#currentIndex = closestIndex;
      this.#updateControls();
    }
  }

  reinit() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#isInitialized = false;
    this.#slidesPerView = this.#calculateSlidesPerView();
    this.#init();
  }

  destroy() {
    this.disconnectedCallback();
  }

  #invalidateOffsets() {
    this.#offsets = null;
    this.#offsetsWidth = null;
  }

  /**
   * Slide offsets, read from the DOM at most once per layout.
   *
   * This used to be read inline inside #findClosestSlideIndex, which runs on every scroll event
   * from both the loop and the non-loop path. Reading `offsetLeft` forces the browser to flush
   * layout, so a carousel of N slides cost N synchronous reflows per scroll event — the reason
   * carousel.js showed 1,618ms of main-thread time against only 234ms of script evaluation.
   *
   * Caching is equivalent rather than approximate: #setupScrollSnap gives every slide
   * `flex: 0 0 calc((100% - (spv - 1) * gap) / spv)`, so offsets are a pure function of the
   * scroller's width and the slide count. Both are invalidated explicitly — the ResizeObserver
   * covers the width, and the length check plus #init/#buildClones cover the count.
   */
  #getSlideOffsets() {
    if (this.#offsets !== null && this.#offsets.length === this.#slides.length) {
      return this.#offsets;
    }

    const offsets = new Array(this.#slides.length);
    for (let i = 0; i < this.#slides.length; i++) {
      offsets[i] = this.#slides[i].offsetLeft;
    }
    this.#offsets = offsets;
    this.#offsetsWidth = this.#scroller.getBoundingClientRect().width;

    return offsets;
  }

  #findClosestSlideIndex(scrollLeft) {
    const offsets = this.#getSlideOffsets();
    let closest = 0;
    let minDist = Infinity;
    for (let i = 0; i < offsets.length; i++) {
      const dist = Math.abs(offsets[i] - scrollLeft);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    return closest;
  }

  #waitForScrollEnd(callback, fallbackMs = 300) {
    let called = false;
    const run = () => {
      if (called) return;
      called = true;
      callback();
    };
    if ('onscrollend' in window) {
      const controller = new AbortController();
      this.#scroller.addEventListener('scrollend', () => {
        controller.abort();
        run();
      }, { signal: controller.signal });
      setTimeout(() => {
        controller.abort();
        run();
      }, fallbackMs);
    } else {
      setTimeout(run, fallbackMs);
    }
  }

  #renderAttributes() {
    const d = parseFloat(this.getAttribute('columns-desktop') ?? '4') || 4;
    const m = parseFloat(this.getAttribute('columns-mobile') ?? '1') || 1;
    const g = parseFloat(this.getAttribute('gap') ?? '16') ?? 16;
    const showA = this.getAttribute('show-arrows') !== 'false';
    const showD = this.getAttribute('show-dots') === 'true';
    const showThumbsArrows = this.getAttribute('show-thumbs-arrows') === 'true';

    // Custom properties need setProperty(). `Object.assign` on a CSSStyleDeclaration only writes a
    // plain JS expando, so these three values never reached the CSSOM and the carousel silently ran
    // on the fallbacks baked into carousel.css. Nothing broke because those fallbacks happen to
    // match, but any carousel whose columns differ from them was being ignored.
    this.style.setProperty('--slides-per-view-desktop', String(d));
    this.style.setProperty('--slides-per-view-mobile', String(m));
    this.style.setProperty('--carousel-gap', `${g}px`);

    const prev = this.querySelector('[name="previous"]');
    const next = this.querySelector('[name="next"]');
    const dots = this.querySelector('.carousel-dots');

    if (prev) prev.toggleAttribute('hidden', !showA);
    if (next) next.toggleAttribute('hidden', !showA);
    if (dots) dots.toggleAttribute('hidden', !showD);

    // Thumbs arrows visibility
    this.querySelectorAll('.carousel-thumbs-arrow').forEach(arrow => {
      arrow.toggleAttribute('hidden', !showThumbsArrows);
    });
  }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', Carousel);
}

export { Carousel };
